import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaSearch, FaDownload, FaEye, FaCheck, FaBan } from 'react-icons/fa';

const purchaseStatusLabel = (status) => {
  if (status === 'completed') return 'Gönderildi';
  if (status === 'pending') return 'Bekliyor';
  if (status === 'cancelled') return 'İptal edildi';
  if (!status) return '—';
  return status;
};

const purchaseStatusClass = (status) => {
  if (status === 'completed') return 'bg-green-500/20 text-green-400 border border-green-500/30';
  if (status === 'pending') return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
  if (status === 'cancelled') return 'bg-red-500/20 text-red-400 border border-red-500/30';
  return 'bg-white/10 text-white/70 border border-white/20';
};

const MarketPurchaseHistory = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const fetchPurchases = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/market/admin/purchases', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: searchTerm || undefined,
          status: filterStatus || undefined
        }
      });
      setPurchases(response.data.purchases || []);
      
      // Calculate stats
      const list = response.data.purchases || [];
      const total = list.length;
      const totalAmount = list.reduce((sum, p) => sum + (p.totalPrice || p.total_price || 0), 0);
      const totalQuantity = list.reduce((sum, p) => sum + (p.quantity || 0), 0);
      const pending = list.filter((p) => p.status === 'pending').length;
      const sent = list.filter((p) => p.status === 'completed').length;
      const cancelled = list.filter((p) => p.status === 'cancelled').length;

      setStats({
        total,
        totalAmount,
        totalQuantity,
        pending,
        sent,
        cancelled
      });
    } catch (error) {
      console.error('Error fetching purchases:', error);
      alert('Siparişler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const handleExportCSV = () => {
    const headers = ['ID', 'Kullanıcı', 'Email', 'Ürün', 'Miktar', 'Toplam Fiyat', 'Durum', 'Tarih'];
    const rows = purchases.map(p => [
      p.id,
      p.user?.username || p.user_username || '-',
      p.user?.email || p.user_email || '-',
      p.product?.name || p.product_name || '-',
      p.quantity || 0,
      p.totalPrice || p.total_price || 0,
      purchaseStatusLabel(p.status),
      p.createdAt || p.created_at || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `market-siparisleri-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openDetailsModal = (purchase) => {
    setSelectedPurchase(purchase);
    setShowDetailsModal(true);
  };

  const handleSetPurchaseStatus = async (purchaseId, newStatus) => {
    const msg =
      newStatus === 'completed'
        ? 'Bu siparişi gönderildi olarak işaretlemek istiyor musunuz?'
        : 'Sipariş iptal edilecek; kullanıcıya puan iadesi yapılır ve stok geri yüklenir. Onaylıyor musunuz?';
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const { data } = await axios.put(
        `/api/market/admin/purchases/${purchaseId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(data.message || 'Güncellendi');
      await fetchPurchases();
      if (selectedPurchase?.id === purchaseId) {
        setSelectedPurchase((prev) =>
          prev && prev.id === purchaseId ? { ...prev, status: newStatus } : prev
        );
      }
    } catch (error) {
      console.error('Update purchase status:', error);
      alert(error.response?.data?.error || 'Durum güncellenemedi');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR').format(price || 0);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gold">Market Sipariş Geçmişi</h2>
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <FaDownload />
          CSV İndir
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500/20 to-green-500/20 rounded-xl p-5 border border-gold/30">
            <div className="text-white/60 text-xs mb-1">TOPLAM SİPARİŞ</div>
            <div className="text-white text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-xl p-5 border border-gold/30">
            <div className="text-white/60 text-xs mb-1">BEKLEYEN</div>
            <div className="text-yellow-400 text-2xl font-bold">{stats.pending}</div>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-5 border border-gold/30">
            <div className="text-white/60 text-xs mb-1">GÖNDERİLDİ</div>
            <div className="text-green-400 text-2xl font-bold">{stats.sent}</div>
          </div>
          <div className="bg-gradient-to-br from-red-500/20 to-rose-500/20 rounded-xl p-5 border border-gold/30">
            <div className="text-white/60 text-xs mb-1">İPTAL</div>
            <div className="text-red-400 text-2xl font-bold">{stats.cancelled}</div>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-5 border border-gold/30 col-span-2 md:col-span-1 lg:col-span-1">
            <div className="text-white/60 text-xs mb-1">TOPLAM TUTAR</div>
            <div className="text-green-400 text-2xl font-bold">{formatPrice(stats.totalAmount)}</div>
            <div className="text-white/40 text-[10px] mt-0.5">Sagu Coin (tüm siparişler)</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-xl p-5 border border-gold/30 col-span-2 md:col-span-2 lg:col-span-1">
            <div className="text-white/60 text-xs mb-1">TOPLAM MİKTAR</div>
            <div className="text-purple-400 text-2xl font-bold">{stats.totalQuantity}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gradient-card rounded-xl p-4 border border-gold/30 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Kullanıcı, email veya ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-navy-800 border border-gold/30 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-navy-800 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
          >
            <option value="">Tüm Durumlar</option>
            <option value="completed">Gönderildi</option>
            <option value="pending">Bekleyen</option>
            <option value="cancelled">İptal Edilen</option>
          </select>
        </div>
      </div>

      {/* Purchases Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold mx-auto"></div>
        </div>
      ) : purchases.length === 0 ? (
        <div className="text-center py-12 text-white/60">
          <p className="text-xl">Henüz sipariş bulunmamaktadır</p>
        </div>
      ) : (
        <div className="bg-gradient-card rounded-xl border border-gold/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">ID</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Kullanıcı</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Ürün</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Miktar</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Toplam Fiyat</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Durum</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Tarih</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => {
                  const product = purchase.product || {};
                  const user = purchase.user || {};
                  const status = purchase.status;

                  return (
                    <tr key={purchase.id} className="border-t border-gold/10 hover:bg-navy-800/30">
                      <td className="px-4 py-3 text-white text-sm font-semibold">#{purchase.id}</td>
                      <td className="px-4 py-3">
                        <div className="text-white text-sm font-semibold">
                          {user.username || purchase.user_username || '-'}
                        </div>
                        <div className="text-white/60 text-xs">
                          {user.email || purchase.user_email || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {(product.imageUrl || product.image_url || purchase.product_image) && (
                            <img
                              src={product.imageUrl || product.image_url || purchase.product_image}
                              alt={product.name || purchase.product_name}
                              className="w-10 h-10 object-contain rounded bg-navy-900"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <div>
                            <div className="text-white text-sm font-semibold">
                              {product.name || purchase.product_name || '-'}
                            </div>
                            <div className="text-white/60 text-xs">
                              {product.type === 'cash' || purchase.product_type === 'cash' ? '💵 Nakit' : '📦 Ürün'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white text-sm">{purchase.quantity || 0}</td>
                      <td className="px-4 py-3 text-gold text-sm font-semibold">
                        {formatPrice(purchase.totalPrice || purchase.total_price || 0)} Sagu Coin
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${purchaseStatusClass(status)}`}>
                          {purchaseStatusLabel(status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-sm">
                        {formatDate(purchase.createdAt || purchase.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {status === 'pending' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSetPurchaseStatus(purchase.id, 'completed')}
                                className="px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold"
                                title="Gönderildi"
                              >
                                <FaCheck />
                                Gönderildi
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSetPurchaseStatus(purchase.id, 'cancelled')}
                                className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold"
                                title="İptal"
                              >
                                <FaBan />
                                İptal
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => openDetailsModal(purchase)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1 text-sm"
                          >
                            <FaEye />
                            Detay
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-card rounded-xl border border-gold/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gold">Sipariş Detayları</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-white/60 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-navy-800/50 rounded-lg p-4">
                  <div className="text-white/60 text-sm mb-1">Sipariş ID</div>
                  <div className="text-white font-semibold">#{selectedPurchase.id}</div>
                </div>

                <div className="bg-navy-800/50 rounded-lg p-4">
                  <div className="text-white/60 text-sm mb-1">Kullanıcı Bilgileri</div>
                  <div className="text-white font-semibold">
                    {selectedPurchase.user?.username || selectedPurchase.user_username || '-'}
                  </div>
                  <div className="text-white/70 text-sm">
                    {selectedPurchase.user?.email || selectedPurchase.user_email || '-'}
                  </div>
                </div>

                <div className="bg-navy-800/50 rounded-lg p-4">
                  <div className="text-white/60 text-sm mb-1">Ürün Bilgileri</div>
                  <div className="flex items-center gap-3">
                    {(selectedPurchase.product?.imageUrl || selectedPurchase.product?.image_url || selectedPurchase.product_image) && (
                      <img
                        src={selectedPurchase.product?.imageUrl || selectedPurchase.product?.image_url || selectedPurchase.product_image}
                        alt={selectedPurchase.product?.name || selectedPurchase.product_name}
                        className="w-20 h-20 object-contain rounded bg-navy-900"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div>
                      <div className="text-white font-semibold">
                        {selectedPurchase.product?.name || selectedPurchase.product_name || '-'}
                      </div>
                      <div className="text-white/70 text-sm">
                        Tip: {selectedPurchase.product?.type === 'cash' || selectedPurchase.product_type === 'cash' ? '💵 Nakit' : '📦 Ürün'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-navy-800/50 rounded-lg p-4">
                    <div className="text-white/60 text-sm mb-1">Miktar</div>
                    <div className="text-white font-semibold text-xl">{selectedPurchase.quantity || 0}</div>
                  </div>
                  <div className="bg-navy-800/50 rounded-lg p-4">
                    <div className="text-white/60 text-sm mb-1">Toplam Fiyat</div>
                    <div className="text-gold font-semibold text-xl">
                      {formatPrice(selectedPurchase.totalPrice || selectedPurchase.total_price || 0)} Sagu Coin
                    </div>
                  </div>
                </div>

                <div className="bg-navy-800/50 rounded-lg p-4">
                  <div className="text-white/60 text-sm mb-1">Durum</div>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${purchaseStatusClass(
                      selectedPurchase.status
                    )}`}
                  >
                    {purchaseStatusLabel(selectedPurchase.status)}
                  </span>
                </div>

                <div className="bg-navy-800/50 rounded-lg p-4">
                  <div className="text-white/60 text-sm mb-1">Sipariş Tarihi</div>
                  <div className="text-white font-semibold">
                    {formatDate(selectedPurchase.createdAt || selectedPurchase.created_at)}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
                {selectedPurchase.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSetPurchaseStatus(selectedPurchase.id, 'completed')}
                      className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                    >
                      <FaCheck />
                      Gönderildi
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetPurchaseStatus(selectedPurchase.id, 'cancelled')}
                      className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                    >
                      <FaBan />
                      İptal et
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 bg-gold hover:bg-yellow-500 text-navy-900 font-semibold rounded-lg transition-colors"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketPurchaseHistory;
